import {
    afterNextRender,
    Component,
    computed,
    input,
    output,
    viewChild,
    ViewEncapsulation,
    inject,
    Injector,
    ChangeDetectionStrategy,
} from "@angular/core";
import { S25BubbleComponent } from "../../standalone/s25.bubble.component";
import { S25TimeBubbleComponent } from "../../standalone/s25.time.bubble.component";
import { UserStore } from "../../stores/user.store";
import { S25Assert } from "../../util/s25.assert";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25VirtualGridAbstractItem } from "../s25-virtual-grid/s25.virtual.grid.abstract.item";
import { MPG } from "./s25.meeting.pattern.grid.component";
import { PopoverComponent } from "../s25-popover/popover.component";
import { StateService } from "../../services/state.service";
import { S25Util } from "../../util/s25-util";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { DowGrid } from "../s25-virtual-grid/s25.dow.grid.component";

@Component({
    selector: "s25-ng-meeting-pattern-grid-event",
    template: `
        <div
            class="item"
            [class.unDraggable]="!item().draggable() || projection().draggable?.() === false || isClipped()"
            [class.hasActiveUser]="activeUser()"
            [class.broughtToFront]="item()._virtualGrid.broughtToFront()"
            [class.sortedBy]="sortedBy()"
            [title]="item().mpg.name"
        >
            @if (showBubbles()) {
                <s25-ng-time-bubble
                    [position]="'up'"
                    [hours]="startHour()!"
                    [is24Hours]="is24Hours()"
                ></s25-ng-time-bubble>
            }
            <div class="item--inner">
                <div class="itemText">{{ itemLabel() }}</div>
                <s25-popover
                    #popover
                    [openTrigger]="'click'"
                    [popoverTemplate]="itemPopoverTemplate"
                    (mousedown)="$event.stopPropagation()"
                    [tabIndex]="0"
                    (keydown.enter)="forceOpen($event, popover)"
                    (keydown.space)="forceOpen($event, popover)"
                    (shown)="onPopoverOpen()"
                    (hidden)="onPopoverClose()"
                    [attr.aria-label]="'Info'"
                    [placement]="'bottom bottom-start bottom-end right right-top right-bottom top top-start top-end'"
                >
                    <s25-ng-icon class="itemCog" [type]="'cog'" [size]="'1.25em'" [ariaLabel]="false" />
                </s25-popover>
                @if (activeUser(); as user) {
                    <s25-ng-bubble [position]="'down'" class="activeUser">
                        {{ user }}
                    </s25-ng-bubble>
                }
            </div>
            @if (showBubbles()) {
                <s25-ng-time-bubble
                    [position]="'down'"
                    [hours]="endHour()!"
                    [is24Hours]="is24Hours()"
                ></s25-ng-time-bubble>
            }
        </div>

        <ng-template #itemPopoverTemplate>
            <div class="qtip-content mpgEventPopover" [tabIndex]="0">
                <div class="eventBox">
                    <div class="boxHeader">
                        <a class="boxHeaderName" (click)="goToItem()">{{ item().mpg.name }}</a>
                    </div>

                    <div class="relevanceSort">
                        <button class="aw-button aw-button--primary" (click)="onRelevanceSort()">
                            Sort Locations By Relevance
                        </button>
                    </div>

                    <div class="boxBody">
                        <div class="flexColumn">
                            <span class="c-dataLabel">Time:</span>
                            <span>{{ startTime() }} - {{ endTime() }}</span>
                        </div>

                        @if (item().mpg.organization) {
                            <div class="flexColumn">
                                <span class="c-dataLabel">Organization:</span>
                                <span>{{ item().mpg.organization!.name }}</span>
                            </div>
                        }

                        @if (item().mpg.organization?.partitions?.length) {
                            <div class="flexColumn">
                                <span class="c-dataLabel">Partition Preferences:</span>
                                <ul>
                                    @for (pref of item().mpg.organization!.partitions; track pref) {
                                        <li>
                                            <strong>Priority: {{ pref.priority }}</strong>
                                            <ul>
                                                @for (partition of pref.list; track partition) {
                                                    <li>
                                                        {{ partition.name }}
                                                    </li>
                                                }
                                            </ul>
                                        </li>
                                    }
                                </ul>
                            </div>
                        }

                        @if (item().mpg.features?.length) {
                            <div class="flexColumn">
                                <span class="c-dataLabel">Features:</span>
                                <ul>
                                    @for (feature of item().mpg.features; track feature) {
                                        <li>{{ feature.name }}</li>
                                    }
                                </ul>
                            </div>
                        }

                        <div class="flexColumn">
                            <span class="c-dataLabel">Enrollment:</span>
                            <span>{{ item().mpg.headCount }}</span>
                        </div>

                        @if (projection().overlappingItems().length) {
                            <div class="flexColumn">
                                <span class="c-dataLabel">Overlapping Items:</span>
                                <ul class="overlappingItems">
                                    <li>
                                        <button
                                            class="bringToFront ngAnchor"
                                            title="Bring To Front"
                                            (click)="bringToFront(item())"
                                        >
                                            {{ item().mpg.name }}
                                        </button>
                                    </li>
                                    @for (overlapItem of projection().overlappingItems(); track overlapItem.id) {
                                        <li>
                                            <button
                                                class="bringToFront ngAnchor"
                                                title="Bring To Front"
                                                (click)="bringToFront(overlapItem)"
                                            >
                                                {{ overlapItem.mpg.name }}
                                            </button>
                                        </li>
                                    }
                                </ul>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            /* The variables used here can be set outside of the component for customization */
            --item-color: var(--grid-item-color, black);
            --item-background: var(--grid-item-background, #cff3d2);
            --item-undraggable-color: var(--grid-item-undraggable-color, white);
            --item-undraggable-background: var(--grid-item-undraggable-background, #616161);
            --item-border: var(--grid-item-border, 1px solid #333333);
        }

        ::ng-deep .nm-party--on s25-ng-meeting-pattern-grid-event {
            /* The variables used here can be set outside of the component for customization */
            --item-color: var(--grid-item-color, black);
            --item-background: var(--grid-item-background, #cff3d2);
            --item-undraggable-color: var(--grid-item-undraggable-color, white);
            --item-undraggable-background: var(--grid-item-undraggable-background, #2e2e2e);
            --item-border: var(--grid-item-border, 1px solid #000000);
        }

        .item {
            --item-height: calc(100% - 3 * var(--border-width));

            background: var(--item-background);
            color: var(--item-color) !important;
            height: var(--item-height);
            border: var(--item-border);
            font-size: var(--font-size-item);
            display: flex;
            translate: 0 1px;
            position: relative;
        }

        .item--inner {
            padding: 2px;
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-self: center;
            align-items: center;
        }

        .item.unDraggable {
            background: var(--item-undraggable-background);
            color: var(--item-undraggable-color) !important;
        }

        .item.hasActiveUser {
            box-shadow: 0px 0px 5px 1px #cff3d2;
        }

        .itemText {
            width: calc(100% - 1.5em);
            overflow: hidden;
            text-overflow: ellipsis;
            height: var(--item-height);
        }

        s25-popover {
            align-self: end;
            cursor: pointer;
            overflow: hidden;
        }

        .flexColumn {
            display: flex;
            flex-direction: column;
        }

        .qtip-content {
            max-height: 50vh;
            overflow-y: scroll;
        }

        ::ng-deep s25-ng-meeting-pattern-grid-event s25-ng-icon .c-svgIcon {
            vertical-align: text-top;
        }

        .relevanceSort {
            text-align: center;
            padding: 1em 0 !important;
        }

        .activeUser {
            position: absolute;
            bottom: 0;
            left: 50%;

            --bubble-background-color: #cff3d2;
        }

        .sortedBy {
            border: 2px dashed red;
        }

        .bringToFront {
            border: none;
            background: transparent;
            cursor: pointer;
        }

        ::ng-deep s25-ng-meeting-pattern-grid .grid--item:has(.broughtToFront) {
            z-index: 2;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25TimeBubbleComponent, PopoverComponent, S25IconComponent, S25BubbleComponent],
})
export class S25MeetingPatternGridEventComponent
    implements S25VirtualGridAbstractItem<MPG.Item, DowGrid.ProjectionData>
{
    readonly item = input.required<MPG.Item>();
    readonly projection = input.required<Grid.ItemProjection<MPG.Item, DowGrid.ProjectionData>>();
    readonly dragging = input(false);
    readonly moving = input(false);
    readonly sortedBy = input(false);
    readonly sortByRelevance = output<MPG.Item>();

    readonly popover = viewChild(PopoverComponent);
    readonly itemLabel = computed(() => {
        const item = this.item();
        const projection = this.projection();
        const sourceDow = item.dowGrid.effective().dow;
        const prefix = projection.role === "shadow" ? `${sourceDow} - ` : "";
        return `${prefix}${item.mpg.name} (${item.mpg.headCount})`;
    });
    readonly startHour = computed(() => this.item().mpg.effective().startHour);
    readonly endHour = computed(() => this.item().mpg.effective().endHour);
    readonly isClipped = computed(() => {
        const clipped = this.projection().window().clipped;
        return clipped.top || clipped.right || clipped.bottom || clipped.left;
    });
    private readonly userStore = inject(UserStore);
    readonly is24Hours = computed(() => this.userStore.is24Hours());
    readonly startTime = computed(() => S25Util.date.toTimeStrFromHours(this.startHour(), this.is24Hours()));
    readonly endTime = computed(() => S25Util.date.toTimeStrFromHours(this.endHour(), this.is24Hours()));
    readonly showBubbles = computed(() => this.dragging());
    readonly activeUser = computed(() => this.item().mpg.activeUser?.());

    returnFocusTo?: HTMLElement;

    private readonly injector = inject(Injector);

    onRelevanceSort() {
        this.sortByRelevance.emit(this.item());
    }

    bringToFront(item: DowGrid.Item<MPG.SourceItem>) {
        for (const overlapItem of item.virtualGrid.overlappingItems() ?? []) {
            overlapItem._virtualGrid.broughtToFront.set(false);
        }
        item._virtualGrid.broughtToFront.set(true);
    }

    forceOpen(event: Event, popover: PopoverComponent) {
        event.preventDefault(); // Prevent spacebar from scrolling page
        popover.forceOpen();
    }

    onPopoverOpen() {
        this.returnFocusTo = document.activeElement as HTMLElement;
        afterNextRender(
            () => {
                (this.popover()?.popoverElement()?.querySelector(".mpgEventPopover") as HTMLElement)?.focus();
            },
            { injector: this.injector },
        );
    }

    onPopoverClose() {
        // Restore focus
        this.returnFocusTo?.focus();
        this.returnFocusTo = undefined;
    }

    goToItem() {
        S25Assert.isDefined(this.item().mpg.event.itemId, "No itemId defined for goToItem");
        StateService.gotoItem({ itemTypeId: 1, itemId: this.item().mpg.event.itemId, forceView: "details" }, true);
    }
}
